/* eslint-disable @typescript-eslint/no-use-before-define */
import { Application, BasePlugin, Device } from '@goodboydigital/astro';
import { BaseTexture, Loader, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent } from './AnalyticsPlugin';

export interface AdsConfig
{
    dailyRewardAds: boolean,
    playtimeRewardAds: boolean,
    preRaceRewardAds: boolean,
    postRaceRewardAds: boolean,
    adCooldown: number
}

export class AdsPlugin implements BasePlugin
{
    public app: App;
    public config!: AdsConfig;
    
    private videoPlaceholder: VideoPlaceholder;
    private videoAdLoaded: boolean;
    public nativeAdLoaded: boolean;
    private lastAdShownTime: number;
    
    private rewardAd: any;
    
    private giveReward: boolean;
    
    private onRewardCallback: ()=> void = ()=>{};
    
    public bannerAdTexture?: Texture;
    
    constructor(app: Application)
    {
        this.app = app as App;
        this.videoPlaceholder = new VideoPlaceholder();
        this.videoPlaceholder.duration = 1000;
        this.videoAdLoaded = false;
        this.nativeAdLoaded = false;
        this.giveReward = false;
        
        this.lastAdShownTime = 0;
    }
    
    public async init(): Promise<void>
    {

    }
    
    public async start(): Promise<void>
    {
        this.config = this.app.assets.getData('ads');
        
    }
    
    public async run(): Promise<void>
    {
        
        if(Settings.isApp() && window.admob){
            await window.admob.start();
            window.admob.configure({
               tagForChildDirectedTreatment: true,
               tagForUnderAgeOfConsent: true,
               maxAdContentRating: "G"
            });
        }
        
        this.loadInitialAds();        
    }
    
    private testTimeout(ms: number): Promise<void>
    {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    /**
     * Play a rewarded video, that will return true if video was fully watched
     * and user got elegible for reward
     * @returns
     */
    public async playRewardedVideo(onRewardCallback: ()=> void): Promise<void>
    {
        if (Settings.noads)
        {
            console.log('[AdsPlugin] Ads disabled!');

            return;
        }
        
        this.app.analytics.trackEvent(AnalyticsEvent.RACE_ADACCEPTANCE_CLICK);
        
        this.giveReward = false;
        
        this.lastAdShownTime = new Date().getTime();
        
        this.onRewardCallback = onRewardCallback;

        this.app.controller.hideAndSuspendApp();
        
        if (!window.admob)
        {
            console.log('[AdsPlugin] Cannot play rewarded ad - admob plugin not available');
            await this.videoPlaceholder.play('INTERSTITIAL VIDEO');
            this.onRewardAdReward();
            this.app.controller.showAndResumeApp();
        }
        else if (!this.videoAdLoaded)
        {
            console.log('[AdsPlugin] Cannot play rewarded ad - video not loaded');
            this.app.controller.showAndResumeApp();
        }
        else{
            console.log('[AdsPlugin] Play rewarded ad');
            
            await this.rewardAd.show();
        }
        
        return;
    }
    
    private async loadRewardedVideo()
    {
        const adUnitId = '/99911224/cn_app/Toon-Trials/rewarded_video';//URLParams.get('adUnitId') || rewardAdUnitId;// '/99911224/cn_app/Toon-Trials/rewarded_video';

        this.videoAdLoaded = false;

        console.log('[AdsPlugin] Loading rewarded ad');
        
        if(!this.rewardAd){
            this.rewardAd = new window.admob.RewardedAd({
                adUnitId: adUnitId,
            });
            
            this.rewardAd.on('load', (evt: any) => {
                this.videoAdLoaded = true;
                console.log('[AdsPlugin] rewarded ad loaded', evt);
            });
            
            this.rewardAd.on('loadfail', (evt: any) => {
                console.log('[AdsPlugin] rewarded ad load fail', evt);
            });
            
            this.rewardAd.on('show', (evt: any) => {
                console.log('[AdsPlugin] rewarded ad show', evt);
            });
            
            this.rewardAd.on('showfail', (evt: any) => {
                console.log('[AdsPlugin] rewarded ad show fail', evt);
                this.app.controller.showAndResumeApp();
            });
            
            this.rewardAd.on('dismiss', (evt: any) => {
                console.log('[AdsPlugin] rewarded ad dismiss', evt);
                if(this.giveReward){
                    this.onRewardAdReward(evt);
                }
                this.giveReward = false;
                this.app.controller.showAndResumeApp();
                this.loadRewardedVideo();
            });
            
            this.rewardAd.on('impression', (evt: any) => {
                console.log('[AdsPlugin] rewarded ad impression', evt);
            });
            
            this.rewardAd.on('reward', (evt: any) => {
                console.log('[AdsPlugin] rewarded ad reward', evt);
                this.giveReward = true;
            });
        }
        
        await this.rewardAd.load();
    }
    
    private async loadNativeAd()
    {
        const adUnitId = "/99911224/cn_app/Toon-Trials";
        const templateId1 = '12140909';

        console.log('[AdsPlugin] Loading native ad');
        
        window.nativeads.loadAd(adUnitId, templateId1, Shared.location.countryId, (data: any) =>
        {
            
            this.getBase64Image(data.url, (imgData: string) => {
                const img = document.createElement('img');
                img.src = `data:image/png;base64,${imgData}`;
                const baseTexture = new BaseTexture(img);
                
                img.onload = () => {
                    const newBaseTexture = new BaseTexture(img);
                    const texture = new Texture(newBaseTexture);
                    this.bannerAdTexture = texture;
                    this.nativeAdLoaded = true;
                }
            });
        }, () =>
        {
            console.log('[AdsPlugin] Error loading native ad');
        });
        
    }
    
    private onRewardAdReward(evt?: any): void
    {
        console.log('[AdsPlugin] rewarded ad reward', evt);
        this.onRewardCallback.call(this);
    }
    
    public loadInitialAds(): void
    {
        // Load app Ads
        if (Settings.isApp())
        {
            if (window.admob) {
                this.loadRewardedVideo();
            }
            if(window.nativeads){
                this.loadNativeAd();
            }
        }
        
    }
    
    public recordNativeAdImpression(): void
    {
        window.nativeads.recordImpression();
        this.loadNativeAd();
    }
    
    public canShowAd(): boolean
    {
        var currentTime = new Date().getTime();
        var adCooldownComplete = this.lastAdShownTime == 0 || ((currentTime - this.lastAdShownTime) / 1000) > this.config.adCooldown;
        return !Settings.noads && !Settings.browserLite && adCooldownComplete;
    }
    
    public getBase64Image(imgUrl: string, callback: (data: string) => void) {

        var img = new Image();
    
        // onload fires when the image is fully loadded, and has width and height
    
        img.onload = function(){
            
          var canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          var ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
          ctx.drawImage(img, 0, 0);
          var dataURL = canvas.toDataURL("image/png"),
              dataURL = dataURL.replace(/^data:image\/(png|jpg);base64,/, "");
    
          callback(dataURL); // the base64 string
    
        };
    
        // set attributes and src 
        img.setAttribute('crossOrigin', 'anonymous'); //
        img.src = imgUrl;
    
    }
}

class VideoPlaceholder
{
    public element: HTMLDivElement;
    public fill: HTMLDivElement;
    public text: HTMLDivElement;
    public duration = 1000;
    public tickInteval = 25;
    public time = 0;
    private interval: any;
    private resolve?: () => void;

    constructor()
    {
        this.element = document.createElement('div');
        this.element.style.position = 'fixed';
        this.element.style.width = '100%';
        this.element.style.height = '100%';
        this.element.style.background = '#999999';

        this.fill = document.createElement('div');
        this.fill.style.position = 'fixed';
        this.fill.style.width = '100%';
        this.fill.style.height = '30px';
        this.fill.style.background = '#FF0000';
        this.fill.style.bottom = '0px';
        this.element.appendChild(this.fill);

        this.text = document.createElement('div');
        this.text.style.position = 'fixed';
        this.text.style.width = '100%';
        this.text.style.top = '40%';
        this.text.style.color = '#EEEEEE';
        this.text.style.fontFamily = 'Roboto,sans-serif';
        this.text.style.fontSize = '32px';
        this.text.style.textAlign = 'center';
        this.text.style.margin = '10px';
        this.element.appendChild(this.text);
    }

    public async play(title: string): Promise<void>
    {
        document.body.appendChild(this.element);
        this.text.innerHTML = title;
        this.time = 0;
        this.tick();
        this.interval = setInterval(this.tick.bind(this), this.tickInteval);

        return new Promise((resolve) =>
        {
            this.resolve = resolve;
        });
    }

    public async stop(): Promise<void>
    {
        clearInterval(this.interval);
        this.interval = undefined;

        if (this.resolve) this.resolve();
        this.resolve = undefined;

        document.body.removeChild(this.element);
    }

    private tick(): void
    {
        const percent = Math.round(this.time / this.duration * 100);

        this.fill.style.width = `${percent}%`;

        this.time += this.tickInteval;

        if (this.time > this.duration) this.stop();
    }
}
